/**
 * Calculates the Levenshtein distance between two strings.
 * The Levenshtein distance is the minimum number of single-character edits (insertions, deletions, or substitutions)
 * required to change one string into another.
 * @param {string} s1 - The first string to compare
 * @param {string} s2 - The second string to compare
 * @returns {number} The Levenshtein distance between the two strings
 */
const levenshteinDistance = (s1, s2) => {
  s1 = s1.toLowerCase()
  s2 = s2.toLowerCase()

  const costs = []
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        costs[j] = j
      } else if (j > 0) {
        let newValue = costs[j - 1]
        if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1
        }
        costs[j - 1] = lastValue
        lastValue = newValue
      }
    }
    costs[s2.length] = lastValue
  }
  return costs[s2.length]
}

/**
 * Calculates the similarity percentage between two strings using Levenshtein distance.
 * The similarity is calculated as: (longerLength - levenshteinDistance) / longerLength * 100
 * @param {string} s1 - The first string to compare
 * @param {string} s2 - The second string to compare
 * @returns {number} The similarity percentage between 0-100, with 2 decimal places
 */
const getSimilarityPercentage = (s1, s2) => {
  const longerLength = Math.max(s1.length, s2.length)
  if (longerLength === 0) {
    return 100.0 // Both strings are empty, consider them 100% similar
  }
  const distance = levenshteinDistance(s1, s2)
  const similarity = (longerLength - distance) / parseFloat(longerLength)
  return parseFloat((similarity * 100).toFixed(2)) // Return percentage with 2 decimal places
}

export { getSimilarityPercentage }
